import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  MAX_TABLE_CAPACITY,
  applySquareSymmetry,
  draftCapacity,
  draftFromTable,
  occupantsBeyond,
  parseSeatInput,
  tableEditRequest,
  validateTableEdit,
} from '../../../lib/seatingTables';
import TableCanvas from './TableCanvas';
import type { TableDraft } from '../../../lib/seatingTables';
import type { SeatingTable, SeatingTableShape, UpdateSeatingTableRequest } from '../../../types/seating';

/**
 * Change a table that is already in the room.
 *
 * Until this existed the only edit was delete-and-rebuild, which unseats
 * everyone: adding two chairs to a round of eight cost the host the ten people
 * they had already placed at it, and a typo in "Table 4" was permanent. The
 * PATCH has been on the API the whole time.
 *
 * The dangerous direction is shrinking. Chairs removed from the end of a table
 * may have people in them, and the API refuses that by default with the list of
 * who would be stranded. The dialog says so while the count is still editable,
 * because the useful moment to learn it is before pressing Save, not in an
 * error afterwards. The reflow escape belongs to the editor, which is where the
 * refusal lands and where the host can be told what it will do to their place
 * cards.
 *
 * `posX`/`posY` are not here on purpose — Studio has no canvas, tables flow by
 * display order, and coordinate boxes would be the itinerary's latitude and
 * longitude fields all over again. Order is the position control this layout
 * actually has.
 */
export default function EditTableDialog({
  table,
  otherLabels,
  tableCount,
  seatPitchCm,
  saving,
  error,
  onClose,
  onSave,
}: {
  table: SeatingTable;
  /** The room's other labels — not this table's own, which is not a collision. */
  otherLabels: string[];
  /** Bounds the order box: "3rd of 14" is a position a host can picture. */
  tableCount: number;
  seatPitchCm: number;
  saving: boolean;
  error: string;
  onClose: () => void;
  onSave: (body: UpdateSeatingTableRequest) => void;
}) {
  const [draft, setDraft] = useState<TableDraft>(() => draftFromTable(table));
  // 1-based on screen, 0-based on the wire. Nobody says "the zeroth table".
  const [position, setPosition] = useState(() => (table.displayOrder ?? 0) + 1);

  const update = (patch: Partial<TableDraft>) =>
    setDraft((prev) => applySquareSymmetry({ ...prev, ...patch }));

  const capacity = draftCapacity(draft);
  const problem = validateTableEdit(draft, otherLabels);
  const stranded = occupantsBeyond(table, capacity);
  const seated = (table.seats ?? []).filter((seat) => seat.assignment).length;
  const body = useMemo(
    () => tableEditRequest(draft, Math.max(position, 1) - 1),
    [draft, position],
  );

  /** Text, not number — see AddTableDialog: "020" cannot be corrected in a number input. */
  const numberField = (
    label: string,
    value: number,
    patch: (next: number) => Partial<TableDraft>,
  ) => (
    <TextField
      label={label}
      type="text"
      size="small"
      value={value}
      onChange={(event) => update(patch(parseSeatInput(event.target.value)))}
      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'aria-label': label }}
      sx={{ width: '100%' }}
    />
  );

  const sideGrid = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(116px, 1fr))',
    gap: 2,
  } as const;

  const fullScreen = useMediaQuery(useTheme().breakpoints.down('sm'));

  return (
    <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" fullScreen={fullScreen}>
      <DialogTitle sx={{ pb: 0.5 }}>Edit {table.label || 'this table'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <ToggleButtonGroup
            exclusive
            size="small"
            value={draft.shape}
            onChange={(_, shape: SeatingTableShape | null) => { if (shape) update({ shape }); }}
          >
            <ToggleButton value="round">Round</ToggleButton>
            <ToggleButton value="square">Square</ToggleButton>
            <ToggleButton value="rectangle">Rectangle / long table</ToggleButton>
          </ToggleButtonGroup>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              size="small"
              label="Table name"
              value={draft.label}
              onChange={(event) => update({ label: event.target.value.slice(0, 120) })}
            />
            <Box sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0 }}>
              <TextField
                label="Position in the room"
                type="text"
                size="small"
                value={position}
                helperText={`of ${tableCount}`}
                onChange={(event) => setPosition(parseSeatInput(event.target.value))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', 'aria-label': 'Position in the room' }}
                sx={{ width: '100%' }}
              />
            </Box>
          </Stack>

          {draft.shape === 'round' ? (
            <Box sx={{ width: { xs: '100%', sm: 160 } }}>
              {numberField('Chairs', draft.seatCount, (seatCount) => ({ seatCount }))}
            </Box>
          ) : draft.shape === 'square' ? (
            <Box sx={sideGrid}>
              {numberField('Top and bottom (each)', draft.seatsTop, (seatsTop) => ({ seatsTop }))}
              {numberField('Left and right (each)', draft.seatsLeft, (seatsLeft) => ({ seatsLeft }))}
            </Box>
          ) : (
            <Box sx={sideGrid}>
              {numberField('Top', draft.seatsTop, (seatsTop) => ({ seatsTop }))}
              {numberField('Bottom', draft.seatsBottom, (seatsBottom) => ({ seatsBottom }))}
              {numberField('Left head', draft.seatsLeft, (seatsLeft) => ({ seatsLeft }))}
              {numberField('Right head', draft.seatsRight, (seatsRight) => ({ seatsRight }))}
            </Box>
          )}

          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">Preview</Typography>
              <Chip size="small" label={`${capacity} seat${capacity === 1 ? '' : 's'}`} />
            </Stack>
            {capacity > 0 && capacity <= MAX_TABLE_CAPACITY ? (
              <TableCanvas
                table={draft}
                seatPitchCm={seatPitchCm}
                ariaLabel={`Preview of ${draft.label || 'this table'}`}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {capacity > MAX_TABLE_CAPACITY
                  ? 'Too many chairs to draw — bring it under 60 and the table comes back.'
                  : 'Add a chair to see the table.'}
              </Typography>
            )}
          </Paper>

          {/*
            Said here, while the number is still editable. The API refuses the
            shrink and names who would be stranded, but a host who learns it
            from an error afterwards has already committed to a decision they
            were never shown.
          */}
          {stranded > 0 ? (
            <Alert severity="warning">
              {stranded === 1
                ? 'One person is sitting in a chair this would remove.'
                : `${stranded} people are sitting in chairs this would remove.`}{' '}
              Saving will be refused unless you move them first — or you can let their
              places be shuffled up into the smaller table when it is.
            </Alert>
          ) : null}

          {seated > 0 && capacity >= seated && stranded === 0 && capacity !== table.capacity ? (
            <Alert severity="info">
              {seated === 1 ? 'One guest keeps their chair.' : `All ${seated} seated guests keep their chairs.`}
              {' '}Chair numbers do not change; this only adds or removes places at the end.
            </Alert>
          ) : null}

          {problem ? <Alert severity="warning">{problem}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" disabled={saving || !!problem} onClick={() => onSave(body)}>
          {saving ? 'Saving…' : 'Save table'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
